import React, { useEffect, useState } from "react";
import {
  Button,
  Box,
  Checkbox,
  FormControlLabel,
  Typography,
  CircularProgress,
  Alert,
} from "@mui/material";
import CommonModal from "../common/CommonModal";
import { useTranslation } from "react-i18next";
import { ApplicationPackage } from "../../hooks/useApplicationPackages";
import { useApplicationServices } from "../../hooks/useApplicationServices";
import { usePackageServiceMapping } from "../../hooks/usePackageServiceMapping";

interface PackageServicesModalProps {
  open: boolean;
  handleClose: () => void;
  packageData: ApplicationPackage | null;
  onSuccess: () => void;
  onError: (message: string) => void;
}

const PackageServicesModal: React.FC<PackageServicesModalProps> = ({
  open,
  handleClose,
  packageData,
  onSuccess,
  onError,
}) => {
  const { t } = useTranslation();
  const { servicesQuery } = useApplicationServices();
  const { mappingQuery, createMappingMutation, updateMappingMutation } = usePackageServiceMapping(
    open && packageData?.id ? packageData.id : null
  );
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [isExistingMapping, setIsExistingMapping] = useState(false);

  useEffect(() => {
    if (open && mappingQuery.isFetched) {
      const ids =
        mappingQuery.data?.servicesList
          ?.map((s) => s.id)
          .filter((id): id is number => id != null) ?? [];
      setSelectedIds(ids);
      setIsExistingMapping(ids.length > 0);
    }
    if (!open) {
      setSelectedIds([]);
      setIsExistingMapping(false);
    }
  }, [open, mappingQuery.isFetched, mappingQuery.data]);

  const handleToggle = (id: number) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const handleSubmit = () => {
    if (!packageData?.id) return;
    const payload = { packageId: packageData.id, serviceIds: selectedIds };
    const mutation = isExistingMapping ? updateMappingMutation : createMappingMutation;

    mutation.mutate(payload, {
      onSuccess: () => {
        onSuccess();
        handleClose();
      },
      onError: (error: unknown) => {
        const err = error as { response?: { data?: { message?: string } }; message?: string };
        onError(err?.response?.data?.message || err?.message || t("error_saving_package_services"));
      },
    });
  };

  const allServices = servicesQuery.data || [];
  const isLoading = servicesQuery.isLoading || mappingQuery.isLoading;
  const isSaving = createMappingMutation.isLoading || updateMappingMutation.isLoading;

  return (
    <CommonModal
      open={open}
      onClose={handleClose}
      title={`${t("manage_package_services")} — ${packageData?.name || ""}`}
      maxWidth="sm"
      customActions={
        <>
          <Button
            onClick={handleClose}
            variant="outlined"
            disabled={isSaving}
            sx={{ borderColor: "#3092e3", color: "#3092e3", "&:hover": { borderColor: "#1a6fb5", color: "#1a6fb5", backgroundColor: "rgba(48,146,227,0.08)" } }}
          >
            {t("cancel")}
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={isSaving || allServices.length === 0}
            sx={{ backgroundColor: "#3092e3", "&:hover": { backgroundColor: "primary.main" } }}
          >
            {isSaving ? t("saving") : t("save")}
          </Button>
        </>
      }
    >
      {isLoading ? (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress />
        </Box>
      ) : allServices.length === 0 ? (
        <Alert severity="info">{t("no_services_available_create_first")}</Alert>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {t("select_services_for_package")}
          </Typography>
          {allServices.map((service) => (
            <FormControlLabel
              key={service.id}
              control={
                <Checkbox
                  checked={service.id != null && selectedIds.includes(service.id)}
                  onChange={() => service.id != null && handleToggle(service.id)}
                  disabled={!service.active}
                />
              }
              label={`${service.name} (${service.code})${!service.active ? ` — ${t("inactive")}` : ""}`}
            />
          ))}
        </Box>
      )}
    </CommonModal>
  );
};

export default PackageServicesModal;
